import { useCallback, useEffect, useMemo, useRef } from 'react';
import pluralize from 'pluralize';
import { toast } from 'react-toastify';

import { calculateItemTotal } from 'utils/common';
import {
  evaluateFormulaDisplayValue,
  expressionFieldFor,
  formatDecimalDisplayValue,
  isFormulaExpressionValue,
  normalizeFormulaPayloadValue,
} from '../utils/rowPayload';

const CLIPBOARD_CACHE_KEY = 'cinebudget:detail-grid-clipboard';
const CLIPBOARD_CACHE_MAX_AGE_MS = 1000 * 60 * 30;

const EXCLUDED_CLIPBOARD_COLUMNS = new Set([
  'checkbox',
  'item_total',
  'fringes',
  'fringe_total',
  'column_total',
]);

const getStableRowKey = row => {
  const rowKey = row?.id ?? row?.row_id;
  return rowKey == null ? '' : String(rowKey);
};

const isFormulaEditableColumn = column =>
  column?.dataType === 'decimal' &&
  column?.type !== 'showValue' &&
  ['quantity', 'rate', 'multiplier'].includes(column.key);

const isClipboardColumn = (column, hiddenColumn = {}) =>
  Boolean(column?.key) &&
  !hiddenColumn[column.key] &&
  !EXCLUDED_CLIPBOARD_COLUMNS.has(column.key) &&
  (column.type === 'text' || column.type === 'select');

const normalizeClipboardRows = text =>
  String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .filter((row, index, rows) => row.length || index < rows.length - 1)
    .map(row => row.split('\t'));

const writeTextFallback = text => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const success = document.execCommand('copy');
  document.body.removeChild(textarea);
  return success;
};

const writeClipboardText = async text => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  if (!writeTextFallback(text)) {
    throw new Error('Clipboard write failed.');
  }
};

const readClipboardText = async () => {
  if (navigator.clipboard?.readText) {
    return navigator.clipboard.readText();
  }
  return '';
};

const isEditableTextElement = element => {
  if (!element) return false;

  const tagName = element.tagName?.toLowerCase();
  return (
    (tagName === 'input' || tagName === 'textarea') &&
    !element.disabled &&
    !element.readOnly &&
    typeof element.selectionStart === 'number' &&
    typeof element.selectionEnd === 'number'
  );
};

const getActiveEditableTextSelection = () => {
  const element = document.activeElement;
  if (!isEditableTextElement(element)) return null;

  const start = element.selectionStart;
  const end = element.selectionEnd;
  return {
    element,
    start,
    end,
    selectedText: element.value.slice(start, end),
  };
};

const setNativeInputValue = (element, value) => {
  const valueSetter = Object.getOwnPropertyDescriptor(element, 'value')?.set;
  const prototypeValueSetter = Object.getOwnPropertyDescriptor(
    Object.getPrototypeOf(element),
    'value'
  )?.set;

  if (prototypeValueSetter && valueSetter !== prototypeValueSetter) {
    prototypeValueSetter.call(element, value);
  } else if (valueSetter) {
    valueSetter.call(element, value);
  } else {
    element.value = value;
  }
};

const replaceActiveEditableTextSelection = text => {
  const selection = getActiveEditableTextSelection();
  if (!selection) return false;

  const { element, start, end } = selection;
  const nextValue = `${element.value.slice(
    0,
    start
  )}${text}${element.value.slice(end)}`;
  const nextCursorPosition = start + text.length;

  setNativeInputValue(element, nextValue);
  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.focus();
  element.setSelectionRange(nextCursorPosition, nextCursorPosition);
  return true;
};

const replaceFocusedEditableCellValue = text => {
  const element = document.activeElement;
  if (!isEditableTextElement(element)) return false;

  const nextValue = String(text ?? '');
  const nextCursorPosition = nextValue.length;

  setNativeInputValue(element, nextValue);
  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.focus();
  element.setSelectionRange(nextCursorPosition, nextCursorPosition);
  return true;
};

const getRowValueForClipboard = (row, column) => {
  if (!row || !column) return '';

  if (isFormulaEditableColumn(column)) {
    const expression = row[expressionFieldFor(column.key)];
    if (expression) return expression;
  }

  return row[column.key] == null ? '' : String(row[column.key]);
};

const getOrderedSelectedRows = (rows, selection) => {
  if (!selection?.columnKey || !selection?.rowKeys?.length) return [];
  const selectedRowKeys = new Set(selection.rowKeys.map(String));
  return rows.filter(row => selectedRowKeys.has(getStableRowKey(row)));
};

const getNumericDefault = (value, fallback = '1') => {
  if (isFormulaExpressionValue(value)) return fallback;
  const parsedValue = Number.parseFloat(
    String(value ?? '')
      .replace(/\$/g, '')
      .replace(/,/g, '')
  );
  return Number.isFinite(parsedValue) && parsedValue >= 0 ? value : fallback;
};

const normalizeRequiredNumbers = row => ({
  ...row,
  quantity: getNumericDefault(row.quantity),
  multiplier: getNumericDefault(row.multiplier),
});

const applyPluralizedUnit = (row, column) => {
  if (!column?.pluralize || !row[column.pluralize.key]) return row;

  if (column.key === 'quantity') {
    return {
      ...row,
      unit:
        Number.parseFloat(row.quantity) <= 1
          ? row.unit?.replace(/s$/, '')
          : pluralize(row.unit, row.quantity),
    };
  }

  if (column.key === 'multiplier') {
    return {
      ...row,
      multiplier_unit:
        Number.parseFloat(row.multiplier) <= 1
          ? row.multiplier_unit?.replace(/s$/, '')
          : pluralize(row.multiplier_unit, row.multiplier),
    };
  }

  return row;
};

const getNextCellValue = ({ column, value, budgetGlobals }) => {
  if (!isFormulaEditableColumn(column)) {
    return {
      [column.key]: value,
    };
  }

  if (isFormulaExpressionValue(value)) {
    const evaluatedValue = evaluateFormulaDisplayValue(value, budgetGlobals);
    if (evaluatedValue === null) return null;

    return {
      [column.key]: evaluatedValue,
      [expressionFieldFor(column.key)]: normalizeFormulaPayloadValue(value),
    };
  }

  return {
    [column.key]: formatDecimalDisplayValue(value),
    [expressionFieldFor(column.key)]: '',
  };
};

const applyCellValueToRow = ({
  row,
  column,
  value,
  budgetGlobals,
  overtimeConfig,
}) => {
  const nextValue = getNextCellValue({
    column,
    value,
    budgetGlobals,
  });

  if (!nextValue) return null;

  let nextRow = normalizeRequiredNumbers({
    ...row,
    ...nextValue,
  });
  nextRow = applyPluralizedUnit(nextRow, column);
  nextRow.item_total = calculateItemTotal(nextRow, overtimeConfig);
  nextRow.column_total =
    parseFloat(nextRow.item_total || 0) + parseFloat(nextRow.fringe_total || 0);
  return nextRow;
};

export const useDetailClipboard = ({
  rows,
  columns,
  hiddenColumn,
  activeCellRef,
  selection,
  addRow,
  clearSelection,
  canEdit,
  registerEditMenuHandlers,
  budgetGlobals,
  overtimeConfig,
  onAddComment,
}) => {
  const editableColumns = useMemo(
    () => columns.filter(column => isClipboardColumn(column, hiddenColumn)),
    [columns, hiddenColumn]
  );

  const columnByKey = useMemo(
    () => new Map(editableColumns.map(column => [column.key, column])),
    [editableColumns]
  );

  const lastTargetRef = useRef(null);

  const buildTargetFromRows = useCallback(
    (columnKey, targetRows) => {
      if (!columnKey || !targetRows?.length || !columnByKey.has(columnKey)) {
        return null;
      }

      const firstRowKey = getStableRowKey(targetRows[0]);
      const startRowIndex = rows.findIndex(
        row => getStableRowKey(row) === firstRowKey
      );

      if (startRowIndex === -1) return null;

      return {
        columnKey,
        startRowIndex,
        targetRows,
        isMultiSelection: targetRows.length > 1,
      };
    },
    [columnByKey, rows]
  );

  const getSelectionTarget = useCallback(() => {
    const selectedRows = getOrderedSelectedRows(rows, selection);
    return buildTargetFromRows(selection?.columnKey, selectedRows);
  }, [buildTargetFromRows, rows, selection]);

  const getFocusedCellTarget = useCallback(() => {
    const focusedElement = document.activeElement;
    if (!isEditableTextElement(focusedElement)) return null;

    const [columnKey, rowIndexText] =
      focusedElement.getAttribute('name')?.split(',') || [];
    const rowIndex = Number.parseInt(rowIndexText, 10);
    if (!columnKey || !Number.isInteger(rowIndex)) return null;

    return buildTargetFromRows(
      columnKey,
      rows[rowIndex] ? [rows[rowIndex]] : []
    );
  }, [buildTargetFromRows, rows]);

  const getActiveCellTarget = useCallback(() => {
    const activeCell = activeCellRef.current;
    if (!activeCell?.columnKey) return null;

    if (activeCell?.rowKeys?.length) {
      const activeRowKeys = new Set(activeCell.rowKeys.map(String));
      const target = buildTargetFromRows(
        activeCell.columnKey,
        rows.filter(row => activeRowKeys.has(getStableRowKey(row)))
      );
      if (target) return target;
    }

    if (Number.isInteger(activeCell?.rowIndex)) {
      return buildTargetFromRows(
        activeCell.columnKey,
        rows[activeCell.rowIndex] ? [rows[activeCell.rowIndex]] : []
      );
    }

    if (!activeCell?.rowKey) return null;

    const activeRow = rows.find(
      row => getStableRowKey(row) === String(activeCell.rowKey)
    );

    return buildTargetFromRows(
      activeCell.columnKey,
      activeRow ? [activeRow] : []
    );
  }, [activeCellRef, buildTargetFromRows, rows]);

  const rememberTarget = useCallback(target => {
    if (!target?.columnKey || !target?.targetRows?.length) return target;

    lastTargetRef.current = {
      columnKey: target.columnKey,
      rowKeys: target.targetRows.map(getStableRowKey).filter(Boolean),
    };

    return target;
  }, []);

  const getCachedTarget = useCallback(() => {
    const cachedTarget = lastTargetRef.current;
    if (!cachedTarget?.columnKey || !cachedTarget?.rowKeys?.length) {
      return null;
    }

    const cachedRowKeys = new Set(cachedTarget.rowKeys.map(String));
    const targetRows = rows.filter(row =>
      cachedRowKeys.has(getStableRowKey(row))
    );

    return buildTargetFromRows(cachedTarget.columnKey, targetRows);
  }, [buildTargetFromRows, rows]);

  const getTargetContext = useCallback(
    ({ preferFocused = false } = {}) => {
      const focusedTarget = getFocusedCellTarget();
      const currentTarget = preferFocused
        ? focusedTarget || getSelectionTarget() || getActiveCellTarget()
        : getSelectionTarget() || focusedTarget || getActiveCellTarget();

      if (currentTarget) return rememberTarget(currentTarget);

      return getCachedTarget();
    },
    [
      getActiveCellTarget,
      getCachedTarget,
      getFocusedCellTarget,
      getSelectionTarget,
      rememberTarget,
    ]
  );

  const getFocusedCellClipboardValue = useCallback(target => {
    const focusedElement = document.activeElement;
    if (
      !target ||
      target.isMultiSelection ||
      !isEditableTextElement(focusedElement)
    ) {
      return null;
    }

    const [columnKey, rowIndexText] =
      focusedElement.getAttribute('name')?.split(',') || [];
    const rowIndex = Number.parseInt(rowIndexText, 10);
    if (target.columnKey !== columnKey || target.startRowIndex !== rowIndex) {
      return null;
    }

    return String(focusedElement.value ?? '');
  }, []);

  const getActiveCellClipboardValue = useCallback(
    target => {
      const activeCell = activeCellRef.current;
      if (
        !target ||
        target.isMultiSelection ||
        target.columnKey !== activeCell?.columnKey ||
        target.startRowIndex !== activeCell?.rowIndex ||
        activeCell?.displayValue === undefined
      ) {
        return null;
      }

      return String(activeCell.displayValue ?? '');
    },
    [activeCellRef]
  );

  useEffect(() => {
    const currentTarget =
      getSelectionTarget() || getFocusedCellTarget() || getActiveCellTarget();
    if (currentTarget) rememberTarget(currentTarget);
  }, [
    getActiveCellTarget,
    getFocusedCellTarget,
    getSelectionTarget,
    rememberTarget,
  ]);

  const saveCachedPayload = useCallback(payload => {
    try {
      localStorage.setItem(
        CLIPBOARD_CACHE_KEY,
        JSON.stringify({
          ...payload,
          createdAt: Date.now(),
        })
      );
    } catch (_error) {
      // Plain text clipboard still works if localStorage is unavailable.
    }
  }, []);

  const getCachedPayload = useCallback(text => {
    try {
      const cached = JSON.parse(
        localStorage.getItem(CLIPBOARD_CACHE_KEY) || 'null'
      );
      if (
        !cached?.createdAt ||
        Date.now() - cached.createdAt > CLIPBOARD_CACHE_MAX_AGE_MS
      ) {
        return null;
      }
      return cached.text === text ? cached : null;
    } catch (_error) {
      return null;
    }
  }, []);

  const applyClipboardMatrix = useCallback(
    (matrix, { preferFocused = false } = {}) => {
      if (!canEdit || !matrix?.length) return;

      const target = getTargetContext({ preferFocused });
      if (!target) {
        toast.info('Select a budget cell before pasting.');
        return;
      }

      const startColumnIndex = editableColumns.findIndex(
        column => column.key === target.columnKey
      );
      if (startColumnIndex === -1) return;

      const useSelectionFill =
        target.isMultiSelection &&
        matrix.length === 1 &&
        matrix[0].length === 1;
      const targetRows = useSelectionFill
        ? target.targetRows
        : rows.slice(
            target.startRowIndex,
            target.startRowIndex + matrix.length
          );

      targetRows.forEach((row, rowOffset) => {
        const sourceValues = useSelectionFill ? matrix[0] : matrix[rowOffset];
        if (!row || !sourceValues?.length) return;

        let nextRow = row;
        sourceValues.forEach((value, columnOffset) => {
          const column = editableColumns[startColumnIndex + columnOffset];
          if (!column) return;

          const updatedRow = applyCellValueToRow({
            row: nextRow,
            column,
            value,
            budgetGlobals,
            overtimeConfig,
          });
          if (updatedRow) nextRow = updatedRow;
        });

        if (nextRow !== row) {
          addRow(nextRow);
        }
      });

      clearSelection?.();
    },
    [
      addRow,
      budgetGlobals,
      overtimeConfig,
      canEdit,
      clearSelection,
      editableColumns,
      getTargetContext,
      rows,
    ]
  );

  const copyCells = useCallback(
    async ({ shouldCut = false } = {}) => {
      const textSelection = getActiveEditableTextSelection();
      if (textSelection?.selectedText) {
        await writeClipboardText(textSelection.selectedText);
        if (shouldCut && canEdit) {
          replaceActiveEditableTextSelection('');
        }
        return;
      }

      const target = getTargetContext();
      if (!target) {
        toast.info('Select a budget cell before copying.');
        return;
      }

      const column = columnByKey.get(target.columnKey);
      if (!column) return;

      const focusedCellValue = getFocusedCellClipboardValue(target);
      const activeCellValue =
        focusedCellValue !== null
          ? focusedCellValue
          : getActiveCellClipboardValue(target);
      const text =
        activeCellValue !== null
          ? activeCellValue
          : target.targetRows
              .map(row => getRowValueForClipboard(row, column))
              .join('\n');

      await writeClipboardText(text);
      saveCachedPayload({
        kind: 'detail-cells',
        text,
        columnKeys: [column.key],
      });

      if (shouldCut && canEdit) {
        applyClipboardMatrix(target.targetRows.map(() => ['']));
      }
    },
    [
      applyClipboardMatrix,
      canEdit,
      columnByKey,
      getActiveCellClipboardValue,
      getFocusedCellClipboardValue,
      getTargetContext,
      saveCachedPayload,
    ]
  );

  const pasteCells = useCallback(async () => {
    if (!canEdit) return;

    const text = await readClipboardText();
    const cachedPayload = getCachedPayload(text);
    const textSelection = getActiveEditableTextSelection();
    if (!cachedPayload && textSelection?.selectedText) {
      if (replaceActiveEditableTextSelection(text)) return;
    }

    const matrix = normalizeClipboardRows(cachedPayload?.text || text);
    const focusedTarget = getFocusedCellTarget();

    if (
      matrix.length === 1 &&
      matrix[0]?.length === 1 &&
      focusedTarget &&
      replaceFocusedEditableCellValue(matrix[0][0])
    ) {
      clearSelection?.();
      return;
    }

    applyClipboardMatrix(matrix, { preferFocused: true });
  }, [
    applyClipboardMatrix,
    canEdit,
    clearSelection,
    getCachedPayload,
    getFocusedCellTarget,
  ]);

  useEffect(() => {
    if (typeof registerEditMenuHandlers !== 'function') return undefined;

    return registerEditMenuHandlers({
      cut: () => {
        copyCells({ shouldCut: true }).catch(() => {
          toast.error('Unable to cut the selected budget cells.');
        });
      },
      copy: () => {
        copyCells().catch(() => {
          toast.error('Unable to copy the selected budget cells.');
        });
      },
      paste: () => {
        pasteCells().catch(() => {
          toast.error('Unable to paste into the selected budget cells.');
        });
      },
      'add-comment': onAddComment,
    });
  }, [copyCells, onAddComment, pasteCells, registerEditMenuHandlers]);

  return null;
};

export default useDetailClipboard;
